import React, { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useHistory, useLocation } from 'react-router-dom'
import { FleetGrid } from '../../../../shared/sso-workspace/fleet-grid'
import { Notice } from '../../../../shared/sso-workspace/fleet-page'
import { Pill } from '../../../../shared/sso-workspace/pill'
import { formatAgo, serverMessage } from '../../../../shared/sso-workspace/format'
import { TableAgInfinite } from '../../../../shared/table-builder/table-ag-infinite'
import { ColumnFormat, ColumnMenu } from '../../../../shared/table-builder/table-columns'
import { Menu, MenuItem, MenuEdit, MenuCopy, MenuDelete } from '../../../../shared/menu/menu'
import { IconList } from '../../../../shared/icon/icon'
import { useAlertRoutingAccess } from './access'
import { listRoutes, routeState, deleteRoute, createRoute } from './alert-routing-api'
import { useCatalog } from './catalog'
import { triggerSummary, filterSummary, whenSummary } from './route-summary'
import { toBody } from './route-editor'
import { ErrorReference } from '../../../../shared/error/error-reference'
import { useErrorLine } from '../../../../shared/error/use-error-line'

// The State column (spec §20.2): an On/Off switch, with Paused beside it. A
// click on the switch is the switch's, never the row's (the grid's shared
// rule, table-builder/row-click.js); the words beside it open the route.
const StateSwitch = ({ route, canEdit, onToggle }) =>
  <span className="ar-state">
    <span className="form-check form-switch m-0">
      <input className="form-check-input" type="checkbox" role="switch" aria-label={`${route.name} on`} checked={Boolean(route.enabled)}
        disabled={!canEdit} onChange={onToggle} />
    </span>
    <span>{route.enabled ? 'On' : 'Off'}</span>
    {route.enabled && route.paused && <Pill tone="warn">Paused</Pill>}
  </span>

export const RoutesTab = () => {
  const catalog = useCatalog()
  const { canEdit, reason } = useAlertRoutingAccess()
  const history = useHistory()
  const category = new URLSearchParams(useLocation().search).get('category') || ''
  const [rows, setRows] = useState(null)
  const [error, failure, setError] = useErrorLine()
  const tableRef = useRef()
  const reload = () => listRoutes(category).then(setRows, err => setError(serverMessage(err, "Routes couldn't be loaded."), err))
  useEffect(() => { reload() }, [category]) // eslint-disable-line react-hooks/exhaustive-deps
  const act = async (r, fn) => { try { await fn(); reload() } catch (err) { setError(serverMessage(err, "That didn't work."), err) } }
  const getRows = useMemo(() => async (start, end) => (rows ? { data: rows.slice(start, end), count: rows.length } : { data: [], count: 0 }), [rows])
  return (
    <>
      {error && <Notice tone="bad" role="alert">{error}<ErrorReference error={failure} /></Notice>}
      {!canEdit && reason && <Notice tone="warn">{reason}</Notice>}
      {canEdit && <Link className="btn sw-btn-primary mb-2" to={`/admin/alert-routing/routes/new?category=${category || 'errors'}`}>New route</Link>}
      {rows && rows.map(r => canEdit && (
        <span key={`q${r.id}`} className="visually-hidden">
          <button type="button" tabIndex={-1} aria-label={`${r.paused ? 'Resume' : 'Pause'} ${r.name}`} onClick={() => act(r, () => routeState(r.id, r.paused ? 'resume' : 'pause'))} />
        </span>
      ))}
      <FleetGrid title="routes" tableRef={tableRef}>
        <TableAgInfinite key={rows ? rows.length + JSON.stringify(rows.map(r => [r.id, r.paused, r.enabled])) : 'x'} ref={tableRef} queryKey="" onGetRows={getRows}
          onRowClicked={r => history.push(`/admin/alert-routing/routes/${r.id}`)}
          emptyState={{ Icon: IconList, title: 'No routes', message: 'Nothing is routed here yet.' }}>
          <ColumnFormat field="name" header="Route" sortable={false} minWidth={180} flex={1.2} render={r => (r.data ? r.value : '')} />
          <ColumnFormat field="category" header="Alerts" sortable={false} minWidth={200} flex={1.4} render={r => (r.data ? triggerSummary(r.data, catalog) : '')} />
          <ColumnFormat field="scopeType" header="Scope" sortable={false} minWidth={120} render={r => (r.data ? (r.value === 'platform' ? 'Platform' : `Organization ${r.data.organizationID}`) : '')} />
          <ColumnFormat field="filters" header="Filters" sortable={false} minWidth={200} flex={1.2} render={r => (r.data ? filterSummary(r.data) : '')} />
          <ColumnFormat field="deliveryMode" header="When" sortable={false} minWidth={220} flex={1.2} render={r => (r.data ? whenSummary(r.data) : '')} />
          <ColumnFormat field="actionTargets" header="Actions" sortable={false} minWidth={180} flex={1.2}
            render={r => (r.data ? (r.value || []).map(t => `${t.name} (${t.type})`).join(', ') : '')} />
          {/* 180: the switch, "On" and the Paused pill (142 px measured in
              WebKit) plus the cell's padding, so the widest state never clips. */}
          <ColumnFormat field="enabled" header="State" sortable={false} minWidth={180} render={r => (r.data
            ? <StateSwitch route={r.data} canEdit={canEdit}
              onToggle={() => act(r.data, () => routeState(r.data.id, r.data.enabled ? 'disable' : 'enable'))} />
            : '')} />
          <ColumnFormat field="lastMatchedAt" header="Last matched" sortable={false} minWidth={130} render={r => (r.data ? (r.value ? formatAgo(r.value) : 'never') : '')} />
          {canEdit && <ColumnMenu menu={r => (
            <Menu small>
              <MenuEdit pin show onClick={() => history.push(`/admin/alert-routing/routes/${r.id}`)} />
              <MenuItem text="test" show onClick={() => history.push(`/admin/alert-routing/routes/${r.id}?step=5`)} />
              <MenuItem text={r.paused ? 'resume' : 'pause'} show onClick={() => act(r, () => routeState(r.id, r.paused ? 'resume' : 'pause'))} />
              <MenuItem text={r.enabled ? 'turn off' : 'turn on'} show onClick={() => act(r, () => routeState(r.id, r.enabled ? 'disable' : 'enable'))} />
              <MenuCopy text="duplicate" show onClick={() => act(r, () => createRoute({ ...toBody({ ...r, name: `${r.name} (copy)` }) }))} />
              <MenuDelete show onClick={() => window.confirm(`Delete the route "${r.name}"? Alerts it would have sent stop now.`) && act(r, () => deleteRoute(r.id))} />
            </Menu>)} />}
        </TableAgInfinite>
      </FleetGrid>
    </>
  )
}
